import { useState, useEffect } from "react";
import { getSessions, getSession, updateSession } from "./supabaseClient";

/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — Pipeline Dashboard
   Route: /pipeline
   
   See all chat sessions. Click into any to view transcript,
   extracted elements, and move through the pipeline.
   ═══════════════════════════════════════════════════════════ */

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Outfit", Helvetica, sans-serif';

const STATUS_COLORS = {
  in_progress: { bg: "rgba(100,180,255,0.12)", text: "rgba(100,180,255,0.9)", label: "In Progress" },
  completed: { bg: "rgba(201,169,110,0.12)", text: "rgba(201,169,110,0.9)", label: "Completed" },
  designing: { bg: "rgba(180,130,255,0.12)", text: "rgba(180,130,255,0.9)", label: "Designing" },
  fulfilled: { bg: "rgba(100,200,130,0.12)", text: "rgba(100,200,130,0.9)", label: "Fulfilled" },
};

const VERTICAL_LABELS = {
  corporate: "Corporate",
  wedding: "Wedding",
  family: "Family",
  team: "Team",
  ib: "Investment Banking",
};

function timeAgo(date) {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default function Pipeline() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filterVertical, setFilterVertical] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Load sessions
  useEffect(() => {
    loadSessions();
  }, [filterVertical, filterStatus]);

  const loadSessions = async () => {
    setLoading(true);
    const filters = {};
    if (filterVertical !== "all") filters.vertical = filterVertical;
    if (filterStatus !== "all") filters.status = filterStatus;
    const data = await getSessions(filters);
    setSessions(data || []);
    setLoading(false);
  };

  const openSession = async (id) => {
    setSelected(id);
    const data = await getSession(id);
    setDetail(data);
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  const changeStatus = async (id, newStatus) => {
    await updateSession(id, { status: newStatus });
    if (detail) setDetail({ ...detail, status: newStatus });
    loadSessions();
  };

  const saveNotes = async (id, notes) => {
    await updateSession(id, { notes });
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0c",
      color: "#fff",
      fontFamily: SANS,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .admin-row:hover { background: rgba(255,255,255,0.03) !important; }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <div style={{
        padding: "20px 30px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <span style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,169,110,0.5)" }}>Yours Truly</span>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.15)", margin: "0 10px" }}>|</span>
          <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(232,230,226,0.7)" }}>Pipeline</span>
        </div>
        <button onClick={loadSessions} style={{
          padding: "6px 16px", borderRadius: 8,
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
          color: "rgba(232,230,226,0.5)", fontSize: 12, cursor: "pointer", fontFamily: SANS,
        }}>
          Refresh
        </button>
      </div>

      {/* ═══ FILTERS ═══ */}
      <div style={{
        padding: "14px 30px",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 11, color: "rgba(232,230,226,0.3)" }}>Filter:</span>
        {["all", "corporate", "wedding", "family", "team", "ib"].map(v => (
          <button key={v} onClick={() => setFilterVertical(v)} style={{
            padding: "4px 12px", borderRadius: 6,
            background: filterVertical === v ? "rgba(201,169,110,0.15)" : "rgba(255,255,255,0.03)",
            border: filterVertical === v ? "1px solid rgba(201,169,110,0.3)" : "1px solid rgba(255,255,255,0.06)",
            color: filterVertical === v ? "rgba(201,169,110,0.9)" : "rgba(232,230,226,0.4)",
            fontSize: 11, cursor: "pointer", fontFamily: SANS,
          }}>
            {v === "all" ? "All" : VERTICAL_LABELS[v] || v}
          </button>
        ))}
        <div style={{ width: 1, height: 16, background: "rgba(255,255,255,0.06)", margin: "0 4px" }} />
        {["all", "in_progress", "completed", "designing", "fulfilled"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: "4px 12px", borderRadius: 6,
            background: filterStatus === s ? "rgba(201,169,110,0.15)" : "rgba(255,255,255,0.03)",
            border: filterStatus === s ? "1px solid rgba(201,169,110,0.3)" : "1px solid rgba(255,255,255,0.06)",
            color: filterStatus === s ? "rgba(201,169,110,0.9)" : "rgba(232,230,226,0.4)",
            fontSize: 11, cursor: "pointer", fontFamily: SANS, textTransform: "capitalize",
          }}>
            {s === "all" ? "All" : STATUS_COLORS[s]?.label || s}
          </button>
        ))}
      </div>

      {/* ═══ SESSIONS LIST ═══ */}
      <div style={{ padding: "0 30px" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "rgba(232,230,226,0.3)", fontSize: 13 }}>Loading...</div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "rgba(232,230,226,0.2)", fontSize: 13 }}>
            No sessions yet. Run a chat at /corporate-create to see data here.
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            {/* Table header */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "140px 100px 1fr 120px 100px 80px",
              padding: "10px 14px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em",
              color: "rgba(232,230,226,0.25)",
            }}>
              <div>When</div>
              <div>Vertical</div>
              <div>Recipient</div>
              <div>Elements</div>
              <div>Status</div>
              <div></div>
            </div>

            {/* Rows */}
            {sessions.map(s => {
              const sc = STATUS_COLORS[s.status] || STATUS_COLORS.in_progress;
              const els = Array.isArray(s.elements) ? s.elements.filter(e => !e.startsWith?.("name:") && !e.startsWith?.("birthday:")) : [];
              return (
                <div
                  key={s.id}
                  className="admin-row"
                  onClick={() => openSession(s.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 100px 1fr 120px 100px 80px",
                    padding: "12px 14px",
                    borderBottom: "1px solid rgba(255,255,255,0.03)",
                    cursor: "pointer",
                    transition: "background 0.15s",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: 12, color: "rgba(232,230,226,0.4)" }}>{timeAgo(s.created_at)}</div>
                  <div style={{ fontSize: 11, color: "rgba(201,169,110,0.5)", textTransform: "capitalize" }}>{s.vertical}</div>
                  <div style={{ fontSize: 13, color: "rgba(232,230,226,0.7)", fontWeight: 500 }}>
                    {s.recipient_name || "—"}
                    {s.occasion && <span style={{ fontSize: 11, color: "rgba(232,230,226,0.25)", marginLeft: 8 }}>{s.occasion}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(232,230,226,0.3)" }}>{els.length} elements</div>
                  <div>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 6,
                      background: sc.bg, color: sc.text,
                    }}>
                      {sc.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(201,169,110,0.4)" }}>View →</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ SESSION DETAIL PANEL ═══ */}
      {detail && (
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(600px, 85vw)",
          background: "#0d0d10",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          overflowY: "auto",
          zIndex: 100,
          boxShadow: "-20px 0 60px rgba(0,0,0,0.5)",
        }}>
          {/* Close bar */}
          <div style={{
            padding: "14px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(232,230,226,0.7)" }}>
              {detail.recipient_name || "Session"}
            </span>
            <button onClick={closeDetail} style={{
              padding: "4px 12px", borderRadius: 6,
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(232,230,226,0.5)", fontSize: 12, cursor: "pointer", fontFamily: SANS,
            }}>
              ✕ Close
            </button>
          </div>

          <div style={{ padding: 20 }}>
            {/* ── Status + Actions ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 10 }}>Status</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["in_progress", "completed", "designing", "fulfilled"].map(s => {
                  const sc = STATUS_COLORS[s];
                  const active = detail.status === s;
                  return (
                    <button key={s} onClick={() => changeStatus(detail.id, s)} style={{
                      padding: "6px 14px", borderRadius: 8,
                      background: active ? sc.bg : "rgba(255,255,255,0.02)",
                      border: active ? `1px solid ${sc.text}` : "1px solid rgba(255,255,255,0.06)",
                      color: active ? sc.text : "rgba(232,230,226,0.3)",
                      fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: SANS,
                    }}>
                      {sc.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Meta ── */}
            <div style={{
              marginBottom: 24, padding: "14px 16px", borderRadius: 12,
              background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, color: "rgba(201,169,110,0.4)", marginBottom: 4 }}>Vertical</div>
                  <div style={{ fontSize: 13, color: "rgba(232,230,226,0.7)", textTransform: "capitalize" }}>{detail.vertical}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "rgba(201,169,110,0.4)", marginBottom: 4 }}>Occasion</div>
                  <div style={{ fontSize: 13, color: "rgba(232,230,226,0.7)" }}>{detail.occasion || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "rgba(201,169,110,0.4)", marginBottom: 4 }}>Recipient</div>
                  <div style={{ fontSize: 13, color: "rgba(232,230,226,0.7)" }}>{detail.recipient_name || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "rgba(201,169,110,0.4)", marginBottom: 4 }}>Created</div>
                  <div style={{ fontSize: 13, color: "rgba(232,230,226,0.7)" }}>{new Date(detail.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* ── Extracted Elements ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 10 }}>
                Extracted Elements ({Array.isArray(detail.elements) ? detail.elements.length : 0})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(Array.isArray(detail.elements) ? detail.elements : []).map((el, i) => {
                  const isName = el.startsWith?.("name:");
                  const isBday = el.startsWith?.("birthday:");
                  return (
                    <span key={i} style={{
                      padding: "5px 12px", borderRadius: 8,
                      background: isName ? "rgba(100,180,255,0.08)" : isBday ? "rgba(180,130,255,0.08)" : "rgba(201,169,110,0.06)",
                      border: isName ? "1px solid rgba(100,180,255,0.2)" : isBday ? "1px solid rgba(180,130,255,0.2)" : "1px solid rgba(201,169,110,0.15)",
                      color: isName ? "rgba(100,180,255,0.8)" : isBday ? "rgba(180,130,255,0.8)" : "rgba(201,169,110,0.7)",
                      fontSize: 12,
                    }}>
                      {el}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* ── Transcript ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 10 }}>
                Transcript
              </div>
              <div style={{
                borderRadius: 12,
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.06)",
                maxHeight: 400, overflowY: "auto",
              }}>
                {(Array.isArray(detail.transcript) ? detail.transcript : []).map((msg, i) => (
                  <div key={i} style={{
                    padding: "10px 14px",
                    borderBottom: "1px solid rgba(255,255,255,0.03)",
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                      color: msg.role === "assistant" ? "rgba(201,169,110,0.5)" : "rgba(100,180,255,0.5)",
                      marginBottom: 4,
                    }}>
                      {msg.role === "assistant" ? "Sculptor" : "Customer"}
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(232,230,226,0.6)", lineHeight: 1.5 }}>
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Notes ── */}
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 10 }}>
                Your Notes
              </div>
              <textarea
                defaultValue={detail.notes || ""}
                onBlur={(e) => saveNotes(detail.id, e.target.value)}
                placeholder="Add notes about this session..."
                style={{
                  width: "100%", minHeight: 80, padding: "10px 14px", borderRadius: 10,
                  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
                  color: "rgba(232,230,226,0.6)", fontSize: 13, fontFamily: SANS,
                  resize: "vertical", outline: "none",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Overlay when detail is open */}
      {detail && (
        <div onClick={closeDetail} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.4)", zIndex: 99,
        }} />
      )}
    </div>
  );
}
